import { Injectable } from "@angular/core";
import { ParamMap } from "@angular/router";

/**
 * sessionStorage key for a discount code captured from a `?coupon=` query
 * parameter (e.g. the FlyGrammar offer link). Session-scoped on purpose so
 * it is never shared across users on a shared machine, and separate from
 * the localStorage-backed `SharedDataService` "discountCode"/"discount"
 * keys the manual payment-page field uses (issue #592, contracts pinned).
 */
const PENDING_COUPON_STORAGE_KEY = "pendingCoupon";

/**
 * Accepted charset/length for an inbound `coupon` value — mirrors the
 * flymsg-be#963 `coupon_code` validation rule. Anything outside this is
 * inert: no pre-fill, no request (issue #592 AC-8).
 */
const COUPON_CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Captures, carries and clears a discount code that arrived on the URL
 * (rather than typed manually), so it survives in-app navigation across the
 * pricing/checkout funnel and, via AuthGuardService's separate
 * `coupon_redirect_url` stash, a sign-in or sign-up round trip.
 *
 * Deliberately independent of whichever discount-code *validator* a payment
 * page uses (`v1/subscriptions/coupon` today, `POST /api/v3/coupons/validate`
 * on the beta payment page behind PR #558) — this service only answers
 * "was a code handed to us on a URL, and what was it", not whether that
 * code is valid.
 */
@Injectable({ providedIn: "root" })
export class PendingCouponService {
  /**
   * Reads `coupon` from a route's query ParamMap (first occurrence wins —
   * `ParamMap.get` already does this for a repeated key — trimmed) and, if
   * it matches the accepted charset/length, stashes it — overwriting
   * whatever was previously stashed, since a code freshly present on the
   * URL always wins over one carried from an earlier visit (product
   * decision, issue #592).
   *
   * A missing or malformed `coupon` param is a no-op: it neither stores nor
   * clears whatever is already stashed, so a code captured on an earlier
   * page in the funnel survives an in-app navigation whose URL doesn't
   * repeat the query string.
   *
   * Returns the freshly captured code, or null if nothing was captured.
   */
  captureFromParams(params: ParamMap | null | undefined): string | null {
    return this.captureRaw(params?.get("coupon"));
  }

  /**
   * Same as captureFromParams, but for a raw redirect URL string (e.g.
   * "/pricing?coupon=LINGUIX50", as AuthGuardService stashes it in
   * `coupon_redirect_url`) instead of a route's ParamMap. Used at every
   * sign-in/sign-up redirect decision so the code is durably stashed even
   * when the navigation that follows is itself intercepted before the
   * target page ever mounts — a brand-new sign-up is bounced to
   * `/session/verify-email` before `/pricing` gets a chance to run
   * `captureFromParams` itself, which otherwise silently lost the code
   * (issue #592 F-3).
   */
  captureFromUrl(url: string | null | undefined): string | null {
    if (!url) {
      return null;
    }

    const queryIndex = url.indexOf("?");
    if (queryIndex === -1) {
      return null;
    }

    let raw: string | null = null;
    try {
      raw = new URLSearchParams(url.slice(queryIndex + 1)).get("coupon");
    } catch {
      return null;
    }

    return this.captureRaw(raw);
  }

  /** Reads the stashed code without clearing it. */
  peek(): string | null {
    try {
      return sessionStorage.getItem(PENDING_COUPON_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** Reads and clears the stashed code in one step. */
  consume(): string | null {
    const code = this.peek();
    this.clear();
    return code;
  }

  /** Drops the stashed code, if any. */
  clear(): void {
    try {
      sessionStorage.removeItem(PENDING_COUPON_STORAGE_KEY);
    } catch {
      // sessionStorage unavailable (private mode, quota) — nothing to clear.
    }
  }

  /**
   * Clears the stashed code once the user has navigated outside the
   * pricing/checkout funnel, so it doesn't linger for a later, unrelated
   * visit to `/pricing` in the same tab (issue #592 AC-6). Called on every
   * completed navigation; matching a funnel URL is a no-op.
   */
  clearIfOutsideFunnel(url: string): void {
    // `/session/verify-email` is part of the funnel too: a brand-new
    // sign-up must pass through it before ever reaching `/pricing`, and the
    // code has to survive that mandatory hop (issue #592 decision 2, F-3).
    if (!/^\/(pricing|plans|new-plan|payment-details|session\/verify-email)(\/|\?|$)/.test(url)) {
      this.clear();
    }
  }

  private captureRaw(raw: string | null | undefined): string | null {
    if (raw == null) {
      return null;
    }

    const trimmed = raw.trim();
    if (!COUPON_CODE_PATTERN.test(trimmed)) {
      return null;
    }

    this.write(trimmed);
    return trimmed;
  }

  private write(code: string): void {
    try {
      sessionStorage.setItem(PENDING_COUPON_STORAGE_KEY, code);
    } catch {
      // sessionStorage unavailable (private mode, quota) — nothing more to do.
    }
  }
}
